import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface TwigPreviewRequest {
  template: string;
  contextType: string;
  entityId?: number | null;
  subscriptionId?: number | null;
}

export type TwigContextBag = Record<string, unknown>;

export interface TwigPreviewResponse {
  rendered: string | null;
  error: string | null;
  context?: TwigContextBag | null;
}

// Narrowing helper that simultaneously rejects non-finite values (NaN, ±Infinity)
// and tells TypeScript the result is `number | undefined` — `Number.isFinite`
// alone is typed as `(v: unknown) => boolean` with no `is` predicate, so a
// future caller passing `string | null` would slip through silently.
function toFiniteNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export async function postTwigPreview(
  scope: RequestScope,
  args: TwigPreviewRequest,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<TwigPreviewResponse>> {
  // An empty template string is a legitimate preview input (render nothing) — only reject non-strings.
  if (typeof args.template !== 'string') {
    return {
      isError: true,
      failureKind: 'failed',
      status: 0,
      errors: { error: 'template is required' },
    };
  }
  if (!args.contextType || !args.contextType.trim()) {
    return {
      isError: true,
      failureKind: 'failed',
      status: 0,
      errors: { error: 'contextType is required' },
    };
  }

  const contextType = args.contextType.trim();

  const body: Record<string, unknown> = {
    template: args.template,
    context_type: contextType,
  };
  // entityId/subscriptionId are forwarded as-is (including 0/negative finite values). The
  // channel API owns value-range validation; the FE just rejects non-finite values (NaN,
  // ±Infinity) so they don't leak into the body or get serialized as the string "NaN" in
  // the Astur subscription header.
  const finiteEntityId = toFiniteNumber(args.entityId);
  const finiteSubscriptionId = toFiniteNumber(args.subscriptionId);
  if (finiteEntityId !== undefined) {
    body.entity_id = finiteEntityId;
  }
  if (finiteSubscriptionId !== undefined) {
    body.subscription_id = finiteSubscriptionId;
  }

  try {
    const res = await scopedChannelApi(scope).post<TwigPreviewResponse>('twig/preview', body, {
      ...options,
      _sessionSemanticRead: true,
      headers: buildAsturHeaders({ subscriptionId: finiteSubscriptionId }),
    });

    const context: TwigContextBag | null =
      res.data && typeof res.data.context === 'object' && res.data.context !== null && !Array.isArray(res.data.context)
        ? (res.data.context as TwigContextBag)
        : null;

    return {
      data: {
        rendered: typeof res.data?.rendered === 'string' ? res.data.rendered : null,
        error: typeof res.data?.error === 'string' ? res.data.error : null,
        context,
      },
      status: res.status,
    };
  } catch (e) {
    // Twig templates are author-provided free text and may contain customer
    // data or internal field references; redact request data so it isn't
    // shipped to Bugsnag on transient backend failures.
    return utils.error.handleAxiosError(e, { redactRequestData: true });
  }
}
